/**
 * Where a page sends feedback (ADR-081's amendment of 2026-10-05, item 5). A development page whose config names
 * the dev server's request endpoint posts there, as client `game` and with both kinds, so reports made while
 * developing still reach the register. Any other page playing an account session posts to its gateway, as client
 * `live-game`, with the session token. A `?token=` session on a page with no dev endpoint has nowhere to send, and
 * its menu holds no Feedback entry. A page served from the local machine may be asked with `?feedback=gateway` to
 * send as a published page does, which the browser suite uses against a gateway of its own.
 */

/**
 * A page's feedback destination: the dev server's endpoint, the gateway's, or none.
 */
export type FeedbackRoute = { readonly kind: "dev"; readonly endpoint: string; readonly client: "game" } | { readonly kind: "gateway"; readonly endpoint: string; readonly client: "live-game" } | null;

/**
 * What the choice reads.
 */
export interface FeedbackRouteFacts {
  /** The request endpoint the page's config names, empty on a published page. */
  readonly devEndpoint: string;
  /** Whether the page plays an account session. */
  readonly account: boolean;
  /** The gateway's HTTP origin, empty when the page knows none. */
  readonly gatewayOrigin: string;
  /** Whether the page is served from the local machine. */
  readonly local: boolean;
  /** The page's `?feedback=` parameter, or null. */
  readonly asked: string | null;
}

/**
 * Chooses a page's feedback destination.
 *
 * @param facts What the page knows.
 * @returns The destination, or null when the page has none.
 * @example
 * feedbackRoute({ devEndpoint: "", account: true, gatewayOrigin: "https://game.odysseytales.net", local: false, asked: null });
 * // { kind: "gateway", endpoint: "https://game.odysseytales.net/requests", client: "live-game" }
 */
export function feedbackRoute(facts: FeedbackRouteFacts): FeedbackRoute {
  const asGateway = facts.local && facts.asked === "gateway";
  if (facts.devEndpoint !== "" && !asGateway) {
    return { kind: "dev", endpoint: facts.devEndpoint, client: "game" };
  }
  if (facts.account && facts.gatewayOrigin !== "") {
    return { kind: "gateway", endpoint: `${facts.gatewayOrigin}/requests`, client: "live-game" };
  }
  return null;
}
